import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./FleetManager.css";
import logoImage from "../../assets/logo_trucktronix.png";

import VehicleCardList from "../../component/vehicles/VehicleCardList";
import EmployeeCardList from "../../component/employee/EmployeeCardList";
import ViewVehicleModal from "../../component/vehicles/ViewVehicleModal";
import ViewEmployeeModal from "../../component/employee/ViewEmployeeModal";
import AddVehicleModal from "../../component/vehicles/AddVehicleModal";
import AddEmployeeModal from "../../component/employee/AddEmployeeModal";
import UpdateVehicleModal from "../../component/vehicles/UpdateVehicleModal";
import UpdateEmployeeModal from "../../component/employee/UpdateEmployeeModal";

import {
  serviceVehicleGetList,
  serviceVehicleDeleteVehicle,
  serviceEmployeeDeleteEmployee,
  serviceEmployeeGetList,
  serviceGetOrganizationDetails,
} from "../../api/serviceFleetMaster";
import { SubscribeButton } from "../../component/payment/SubscribeButton";

const SearchBox = ({ value, onChange, placeholder, active }) => (
  <div className={`fm-search-container ${active ? "is-active" : ""}`}>
    <i className="bi bi-search search-icon" />
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
    {value && (
      <button className="fm-search-clear" onClick={() => onChange("")}>
        <i className="bi bi-x-circle-fill" />
      </button>
    )}
  </div>
);

const FleetManager = () => {
  const navigate = useNavigate();

  const [organizationId, setOrganizationId] = useState(null);

  const fetchOrganization = useCallback(async () => {
    try {
      const data = await serviceGetOrganizationDetails();

      setOrganizationId(data._id);
    } catch (err) {
      console.error("Failed to fetch admin profile:", err);
      toast.error(`Error: ${err?.message ?? "Unknown to load profile"}`);
      setOrganizationId(null);
    }
  }, []);

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrganization]);

  const [activeTab, setActiveTab] = useState("vehicle");
  const [vehicleList, setVehicleList] = useState([]);

  //for vehicle subscription selections
  const [subscribeVehicleIds, setSubscribeVehicleIds] = useState(new Set());

  const [loadingVehicle, setLoadingVehicle] = useState(true);
  const [employeeList, setEmployeeList] = useState([]);
  const [loadingEmployee, setLoadingEmployee] = useState(true);

  const [vehicleQuery, setVehicleQuery] = useState("");
  const [employeeQuery, setEmployeeQuery] = useState("");

  const [panel, setPanel] = useState({
    type: "empty", //empty, vehicle, employee
    mode: "view", //view, edit, add
    payload: null, //vehicle, employee or null for selected
  });
  const sheetOpen = useMemo(() => panel.type !== "empty", [panel.type]);

  const closePanel = () =>
    setPanel({ type: "empty", mode: "view", payload: null });

  // ---- service handlers ----
  const serverEmployeeGetList = async () => {
    try {
      setLoadingEmployee(true);

      const dataList = await serviceEmployeeGetList();

      if (dataList) {
        setEmployeeList(dataList);
      }
    } catch (error) {
      toast.error(error.message || "Error: Contact Trucktronix!");
      setEmployeeList([]);
    } finally {
      setLoadingEmployee(false);
    }
  };

  const serverVehicleGetList = useCallback(async () => {
    try {
      setLoadingVehicle(true);

      const dataList = await serviceVehicleGetList();
      const vehicles = Array.isArray(dataList) ? dataList : [];

      const unsubscribedIds = new Set(
        vehicles
          .filter(
            (vehicle) =>
              vehicle.subscriptionStatus !== "ACTIVE" &&
              vehicle.subscriptionStatus !== "TRIAL",
          )
          .map((vehicle) => vehicle._id)
          .filter(Boolean),
      );

      setVehicleList(vehicles);
      setSubscribeVehicleIds(unsubscribedIds);
    } catch (error) {
      toast.error(error.message || "Error: Contact Trucktronix!");
      setVehicleList([]);
    } finally {
      setLoadingVehicle(false);
    }
  }, []);

  const serverEmployeeDeleteEmployee = async (employeeId) => {
    try {
      await serviceEmployeeDeleteEmployee(employeeId);

      setEmployeeList((prev) => prev.filter((e) => e._id !== employeeId));

      toast.success("Employee deleted");

      if (panel.type === "employee" && panel.payload?._id === employeeId) {
        closePanel();
      }
    } catch (error) {
      if (error.message.includes("404")) {
        toast.error("Employee not found");
      } else {
        toast.error(error.message || "Error: Contact Trucktronix!");
      }
    }
  };

  const serverVehicleDeleteVehicle = async (vehicleId) => {
    try {
      await serviceVehicleDeleteVehicle(vehicleId);

      setVehicleList((prev) => prev.filter((v) => v._id !== vehicleId));

      toast.success("Vehicle deleted!");

      if (panel.type === "vehicle" && panel.payload?._id === vehicleId) {
        closePanel();
      }
    } catch (error) {
      if (error.message.includes("404")) {
        toast.error("Vehicle not found");
      } else {
        toast.error(error.message || "Error: Contact Trucktronix!");
      }
    }
  };

  // this is for dropdown list in add new employee to associate with vehicle
  const vehicleOptions = useMemo(
    () =>
      vehicleList.map((vehicle) => ({
        value: vehicle._id, // ObjectId
        label: vehicle.vehicleCode, // REGO
      })),
    [vehicleList],
  );

  useEffect(() => {
    // kick both list components once so they load and set setVehicleList/setEmployeeList
    serverVehicleGetList();
    serverEmployeeGetList();
  }, []);

  const toggleVehicleSelection = (vehicleId) => {
    setSubscribeVehicleIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }

      return newSet;
    });
  };

  const refreshVehicleList = async () => {
    await serverVehicleGetList();
  };

  const renderDetailContent = () => {
    if (panel.type === "empty") {
      return (
        <div className="fm-empty-state">
          <div className="fm-empty-visual">
            <i className="bi bi-shield-check" />
            <div className="fm-pulse-ring" />
          </div>
          <h3 className="fm-empty-title">Fleet Manager</h3>
          <p className="fm-empty-text">
            Please select a vehicle or employee to view details.
          </p>
          <div className="fm-empty-stats">
            <div className="fm-stat-box">
              <span className="label">Vehicles</span>
              <span className="count">{vehicleList.length}</span>
            </div>
            <div className="fm-stat-box">
              <span className="label">Employees</span>
              <span className="count">{employeeList.length}</span>
            </div>
          </div>
        </div>
      );
    }

    const isVehicle = panel.type === "vehicle";
    const ViewComp = isVehicle ? ViewVehicleModal : ViewEmployeeModal;
    const EditComp = isVehicle ? UpdateVehicleModal : UpdateEmployeeModal;
    const AddComp = isVehicle ? AddVehicleModal : AddEmployeeModal;

    if (panel.mode === "view")
      return (
        <ViewComp
          key={panel.payload?._id}
          {...{ [panel.type]: panel.payload }}
          onClose={closePanel}
          onUpdateVehicleClick={(v) =>
            setPanel({ type: "vehicle", mode: "edit", payload: v })
          }
          onUpdateEmployeeClick={(e) =>
            setPanel({ type: "employee", mode: "edit", payload: e })
          }
          handleDeleteEmployeeClick={serverEmployeeDeleteEmployee}
          handleDeleteVehicleClick={serverVehicleDeleteVehicle}
          onVehicleUpdated={refreshVehicleList}
        />
      );
    if (panel.mode === "edit")
      return (
        <EditComp
          {...{ [panel.type]: panel.payload }}
          onClose={closePanel}
          onUpdate={async () => {
            if (isVehicle) await serverVehicleGetList();
            else await serverEmployeeGetList();
          }}
          vehicleOptions={vehicleOptions}
        />
      );
    return (
      <AddComp
        onClose={closePanel}
        onVehicleAdded={async () => await serverVehicleGetList()}
        onEmployeeAdded={async () => await serverEmployeeGetList()}
        vehicleOptions={vehicleOptions}
      />
    );
  };

  return (
    <div className="fm-page">
      {/* floater add */}
      <button
        className="fm-btn-floater"
        title={`Add ${activeTab}`}
        onClick={() =>
          setPanel({ type: activeTab, mode: "add", payload: null })
        }
      >
        {activeTab === "vehicle" ? (
          <i className="bi bi-truck" />
        ) : (
          <i className="bi bi-person" />
        )}
        <span>Add</span>
      </button>

      {/* HEADER: Original Navy/Blue Gradient & Logo Right */}
      <header className="fm-header">
        <button
          type="button"
          className="root-btn-back"
          onClick={() => navigate("/")}
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Back
        </button>
        <div className="fm-header-title">
          <div className="fm-header-label">
            <span>Fleet Manager</span>
            <span>Manage vehicles & employees</span>
          </div>
          <img src={logoImage} alt="Trucktronix" className="fm-logo" />
        </div>
      </header>

      <main className="fm-main">
        <div className={`fm-layout ${sheetOpen ? "panel-open" : ""}`}>
          {/* LEFT SECTION */}
          <aside className="fm-sidebar">
            <div className="fm-sidebar-header">
              <div
                className={`fm-toggle-track ${
                  activeTab === "vehicle"
                    ? "is-v"
                    : activeTab === "employee"
                      ? "is-e"
                      : "is-t"
                }`}
              >
                <button
                  className={activeTab === "vehicle" ? "active" : ""}
                  onClick={() => setActiveTab("vehicle")}
                >
                  Vehicles
                </button>
                <button
                  className={activeTab === "employee" ? "active" : ""}
                  onClick={() => setActiveTab("employee")}
                >
                  Employees
                </button>
                {/* <button
                  className={activeTab === "tracker" ? "active" : ""}
                  onClick={() => setActiveTab("tracker")}
                >
                  Trackers
                </button> */}
              </div>
              <SearchBox
                value={activeTab === "vehicle" ? vehicleQuery : employeeQuery}
                onChange={
                  activeTab === "vehicle" ? setVehicleQuery : setEmployeeQuery
                }
                placeholder={`Search ${activeTab}...`}
                active={
                  !!(activeTab === "vehicle" ? vehicleQuery : employeeQuery)
                }
              />
            </div>
            <div className="fm-list-area">
              {activeTab === "vehicle" ? (
                <VehicleCardList
                  isLoading={loadingVehicle}
                  vehicles={vehicleList}
                  onVehicleClick={(v) =>
                    setPanel({ type: "vehicle", mode: "view", payload: v })
                  }
                  externalQuery={vehicleQuery}
                  selectedIds={subscribeVehicleIds}
                  onToggleSelect={toggleVehicleSelection}
                />
              ) : (
                <EmployeeCardList
                  isLoading={loadingEmployee}
                  employees={employeeList}
                  onEmployeeClick={(e) =>
                    setPanel({ type: "employee", mode: "view", payload: e })
                  }
                  externalQuery={employeeQuery}
                />
              )}
            </div>
            <SubscribeButton
              vehicleList={vehicleList}
              organizationId={organizationId}
              selectedVehicleIds={subscribeVehicleIds}
              onSubscriptionUpdate={refreshVehicleList}
            />
          </aside>

          {/* RIGHT SECTION */}
          <section className="fm-panel-container">
            <div className="fm-mobile-handle">
              <span />
            </div>
            <div className="fm-panel-content">{renderDetailContent()}</div>
          </section>

          {sheetOpen && <div className="fm-overlay" onClick={closePanel} />}
        </div>
      </main>
    </div>
  );
};

export default FleetManager;
